import Box from "@amzn/awsui-components-react/polaris/box";
import Button from "@amzn/awsui-components-react/polaris/button";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Container from "@amzn/awsui-components-react/polaris/container";
import ContentLayout from "@amzn/awsui-components-react/polaris/content-layout";
import ExpandableSection from "@amzn/awsui-components-react/polaris/expandable-section";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import "./styles.css";

// Screenshots live in public/how-to-use and are generated from the real UI
// with sample data. BASE_URL keeps the paths working under Vite's base "./".
const shot = (name: string) => `${import.meta.env.BASE_URL}how-to-use/${name}`;

interface ScreenshotProps {
  src: string;
  alt: string;
  caption: string;
}

function Screenshot({ src, alt, caption }: ScreenshotProps) {
  return (
    <figure className="howto-figure">
      <img className="howto-image" src={src} alt={alt} loading="lazy" />
      <figcaption>
        <Box variant="small" color="text-body-secondary">
          {caption}
        </Box>
      </figcaption>
    </figure>
  );
}

interface StepProps {
  number: number;
  title: string;
  children: React.ReactNode;
}

function Step({ number, title, children }: StepProps) {
  return (
    <Container
      header={
        <Header variant="h2">
          <span className="howto-step-number" aria-hidden="true">
            {number}
          </span>
          Step {number}: {title}
        </Header>
      }
    >
      <SpaceBetween size="m">{children}</SpaceBetween>
    </Container>
  );
}

export default function HowToUse() {
  return (
    <div className="howto-page">
      <ContentLayout
        header={
          <Header
            variant="h1"
            description="Everything a new site needs to run a daily Gemba with DigiGemba."
            actions={
              <Button href="#/" iconName="arrow-left">
                Back to sign in
              </Button>
            }
          >
            How to use DigiGemba
          </Header>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">What is DigiGemba?</Header>}>
            <SpaceBetween size="s">
              <Box variant="p">
                DigiGemba is a digital Gemba board. Your team records four
                kinds of items during the shift, then presents them as a
                slideshow at the daily stand-up instead of using a paper or
                whiteboard Gemba.
              </Box>
              <ColumnLayout columns={4} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Success Stories</Box>
                  <Box variant="p">Wins and recognition from the shift.</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Safety</Box>
                  <Box variant="p">Safety observations and follow-ups.</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">
                    Associate Barriers - JDI
                  </Box>
                  <Box variant="p">
                    Issues raised by associates, with owner, due date and
                    progress.
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Top Focus Areas</Box>
                  <Box variant="p">
                    Larger improvement projects your site is driving.
                  </Box>
                </div>
              </ColumnLayout>
            </SpaceBetween>
          </Container>

          <Container
            header={
              <Header
                variant="h2"
                description="Your onboarding request is done. Here is how to go live."
              >
                New site? Start here
              </Header>
            }
          >
            <SpaceBetween size="m">
              <ol className="howto-list">
                <li>
                  <strong>Check that your site is in the Site list</strong> on
                  the sign-in page (Step 1). Once onboarding is complete your
                  site code appears there. If it is missing, contact the
                  DigiGemba team.
                </li>
                <li>
                  <strong>Make sure everyone who will use DigiGemba has
                  access.</strong> Sign-in is limited to approved groups. Each
                  person (or their team group) must be added before they can
                  sign in at{" "}
                  <a href="https://digigemba.app.amazon.dev">
                    digigemba.app.amazon.dev
                  </a>
                  . Request access with a SIM ticket to{" "}
                  <strong>Distribution center / OLM1 ICQA / Data Requests</strong>{" "}
                  and include the logins or group names to add.
                </li>
                <li>
                  <strong>Add a few items in each category</strong> before
                  your first stand-up (Step 3) so the board is not empty.
                </li>
                <li>
                  <strong>Run your first Gemba</strong> with the Presentation
                  (Steps 4 and 5). That slideshow is the visual board your team
                  sees.
                </li>
              </ol>
              <Box variant="p">
                <strong>Is training required?</strong> The app itself needs no
                setup or certification on your side. This page walks through
                the full daily workflow. A short dry run of Steps 1 to 5 with
                your leads before going live is a good way to get comfortable.
              </Box>
              <Box variant="p">
                <strong>How do we see the board?</strong> Connect a laptop to a
                big TV or projector at your stand-up area and start the
                Presentation in Step 5. See{" "}
                <strong>Running the stand-up on a big screen</strong> below.
              </Box>
            </SpaceBetween>
          </Container>

          <Step number={1} title="Choose your site and shift">
            <Box variant="p">
              After you sign in with Midway, pick your <strong>Site</strong>{" "}
              and <strong>Shift</strong>, then select{" "}
              <strong>Let's Go</strong>. Items are saved and shown per site and
              shift, so pick the shift you are working. Your choice is
              remembered on this computer.
            </Box>
            <ColumnLayout columns={2} variant="text-grid">
              <ul className="howto-list">
                <li>FHD: Front Half Days</li>
                <li>FHN: Front Half Nights</li>
                <li>BHD: Back Half Days</li>
                <li>BHN: Back Half Nights</li>
                <li>Support: support teams</li>
              </ul>
              <Box variant="p" color="text-body-secondary">
                To switch site or shift later, use <strong>Sign Out</strong>{" "}
                at the bottom of the left menu and choose again.
              </Box>
            </ColumnLayout>
            <Screenshot
              src={shot("01-login.png")}
              alt="DigiGemba sign-in page with the Site and Shift dropdowns and the Let's Go button"
              caption="Sign-in page: choose Site and Shift, then Let's Go."
            />
          </Step>

          <Step number={2} title="Find your way around the home page">
            <Box variant="p">
              The home page shows the latest items for your site and shift in
              four sections. Use <strong>View all</strong> under a section to
              see the full list. The left menu has the same categories plus{" "}
              <strong>Add</strong>, <strong>Presentation</strong>,{" "}
              <strong>Statistics</strong> and <strong>Quick Summary</strong>.
            </Box>
            <Screenshot
              src={shot("02-home.png")}
              alt="DigiGemba home page with the left menu and the Success Stories, Safety, Associate Barriers and Top Focus Areas sections"
              caption="Home page: left menu, the four Gemba sections, and the presentation panel on the right."
            />
          </Step>

          <Step number={3} title="Add an item">
            <Box variant="p">
              Select <strong>Add</strong> in a section, or open{" "}
              <strong>Add</strong> in the left menu. Fill in the detail, date,
              shift and department, and attach a photo if you have one.
              Barriers and Top Focus Areas also ask for an owner, process, due
              date and priority.
            </Box>
            <Box variant="p">
              Select <strong>Rewrite with AI</strong> to clean up the wording of
              the detail. You can undo the rewrite if you prefer your original
              text.
            </Box>
            <Screenshot
              src={shot("03-add-story.png")}
              alt="Add Success Story form with the Story Detail field and the Rewrite with AI button"
              caption="Add form: write the detail, optionally use Rewrite with AI, then Submit."
            />
          </Step>

          <Step number={4} title="Choose what to present">
            <Box variant="p">
              On the home page, tick the checkbox next to each item you want
              to talk about, then select <strong>Add to Presentation</strong>{" "}
              in that section. Chosen items appear in the panel on the right.
              Repeat for Success Stories, Safety and Associate Barriers. Top
              Focus Areas are picked automatically (see the FAQ below).
            </Box>
            <Screenshot
              src={shot("04-select-items.png")}
              alt="Home page with two success stories selected and listed in the presentation panel on the right"
              caption="Selected items are listed in the right panel, ready for the presentation."
            />
          </Step>

          <Step number={5} title="Run the Gemba presentation">
            <Box variant="p">
              Select <strong>Presentation</strong> in the left menu (or the
              button at the top of the right panel). On{" "}
              <strong>Config Presentation</strong>, add the presenters and
              their process, optionally turn on <strong>Metric</strong> to show
              today's goals, check the chosen items, then select{" "}
              <strong>Start Presentation</strong>.
            </Box>
            <Screenshot
              src={shot("05-config.png")}
              alt="Config Presentation page with Presenter, Metric and Chosen Items sections and the Start Presentation button"
              caption="Config Presentation: presenters, metrics and chosen items."
            />
            <Box variant="p">
              The presentation opens full screen on an intro slide, then goes
              through Success Stories, Safety, Associate Barriers and Top Focus
              Areas, and ends with a Thank you slide. Use the left and right
              arrow keys, or the buttons on each slide, to move between slides.
              If you leave full screen, select <strong>Go Fullscreen</strong>{" "}
              on the intro slide.
            </Box>
            <Screenshot
              src={shot("06-presentation.png")}
              alt="Presentation intro slide shown on screen during the stand-up"
              caption="The presentation is the visual board your team sees during stand-up."
            />
            <Box variant="p">
              On the Thank you slide, select <strong>Export to CSV</strong> to
              download a copy of what was presented: presenters, metrics, and
              every Success Story, Safety item, Associate Barrier and Top Focus
              Area shown, one row per item. When the file is saved, the button
              changes to a green check with{" "}
              <strong>Saved in Downloads folder</strong>. Export before you
              select{" "}
              <strong>Back to Homepage</strong>, because that clears the chosen
              items.
            </Box>
          </Step>

          <Step number={6} title="Track progress">
            <Box variant="p">
              <strong>Statistics</strong> opens the Gemba Walk Report: new and
              closed barriers compared with the prior Gemba, and open JDIs by
              priority and shift.{" "}
              <strong>Quick Summary</strong> lists the selected site's items
              for a date across all shifts, labels every item with its shift,
              and <strong>Generate Quick Summary</strong> writes a short recap
              that identifies the shift for every issue or highlight. Both are
              in the left menu and are hidden when the shift is set to Support.
            </Box>
            <Screenshot
              src={shot("07-statistics.png")}
              alt="Statistics page showing the Gemba Walk Report with new barriers, closed barriers and open JDIs by priority"
              caption="Statistics: the Gemba Walk Report for your site."
            />
          </Step>

          <Container
            header={
              <Header
                variant="h2"
                description="A typical day with DigiGemba, from shift start to follow-up."
              >
                Daily workflow
              </Header>
            }
          >
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">During the shift</Box>
                <ul className="howto-list">
                  <li>Add wins, safety observations and barriers as they happen (Step 3).</li>
                  <li>Attach a photo when it helps explain the item.</li>
                </ul>
              </div>
              <div>
                <Box variant="awsui-key-label">10 minutes before stand-up</Box>
                <ul className="howto-list">
                  <li>Choose the items to discuss (Step 4).</li>
                  <li>Open Presentation, add presenters and today's goals (Step 5).</li>
                  <li>Connect the laptop to the big screen and test it.</li>
                </ul>
              </div>
              <div>
                <Box variant="awsui-key-label">At and after stand-up</Box>
                <ul className="howto-list">
                  <li>Select Start Presentation and walk through each section.</li>
                  <li>On the Thank you slide, select Export to CSV to keep a copy.</li>
                  <li>Update barrier owners, progress and due dates afterwards.</li>
                  <li>Review Statistics to see what was closed (Step 6).</li>
                </ul>
              </div>
            </ColumnLayout>
          </Container>

          <Container
            header={
              <Header
                variant="h2"
                description="Make sure everyone at the stand-up can read the board."
              >
                Running the stand-up on a big screen
              </Header>
            }
          >
            <SpaceBetween size="s">
              <Box variant="p">
                After you finish the setup in Step 5 and select{" "}
                <strong>Start Presentation</strong>, show it on a big TV or
                projector so the whole team can see it clearly.
              </Box>
              <ol className="howto-list">
                <li>
                  <strong>Connect the laptop</strong> to the TV or projector
                  with an HDMI or USB-C cable, and switch the TV to that input.
                </li>
                <li>
                  <strong>Show the laptop screen on the TV.</strong> On
                  Windows, press <strong>Windows + P</strong> and choose{" "}
                  <strong>Duplicate</strong>. On a Mac, open System Settings,
                  then Displays, and turn on mirroring.
                </li>
                <li>
                  <strong>Start the presentation.</strong> It fills the screen
                  automatically. Press <strong>Esc</strong> to leave full
                  screen.
                </li>
                <li>
                  <strong>Check it from the back of the group.</strong> If text
                  is hard to read, zoom with <strong>Ctrl +</strong> (
                  <strong>Cmd +</strong> on a Mac) before starting.
                </li>
              </ol>
              <Box variant="awsui-key-label">Tips</Box>
              <ul className="howto-list">
                <li>
                  Set up and test the screen a few minutes early, not when the
                  team is already waiting.
                </li>
                <li>
                  Plug in the charger and turn on Do Not Disturb so chat and
                  email pop-ups don't appear on the TV.
                </li>
                <li>
                  Keep the laptop where the presenter can reach it, and use the
                  arrow keys to move between slides. A presentation clicker
                  works only if it sends arrow keys.
                </li>
                <li>
                  If the screen goes black, check the cable and the TV input
                  first, then press Windows + P again.
                </li>
              </ul>
            </SpaceBetween>
          </Container>

          <Container header={<Header variant="h2">FAQ</Header>}>
            <SpaceBetween size="s">
              <ExpandableSection headerText="Why don't I see items my team added?">
                Items are stored per site and shift. Check that you picked the
                same site and shift as the person who added them. Use Sign Out
                in the left menu to choose again.
              </ExpandableSection>
              <ExpandableSection headerText="Why can't I choose which Top Focus Areas to present?">
                This is by design. You don't select Top Focus Areas manually.
                The presentation automatically shows the top 5 open items (On
                Track, Not Started or Off Track), sorted by priority and then by
                most recent date. They appear right after Associate Barriers.
                To change what is shown, update the priority or progress of the
                item. Select the (i) icon next to Top Focus Areas on the home
                page for a quick reminder.
              </ExpandableSection>
              <ExpandableSection headerText="Can I edit or delete an item?">
                Yes. Open the full list with View all (or the category in the
                left menu), select the item, and use Update or Delete at the
                top of the table.
              </ExpandableSection>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </ContentLayout>
    </div>
  );
}
